import { debounce } from 'perfect-debounce';
import Vue from 'vue';
import {
    getEhsCode,
    getMarvelRCode,
    getMg3Code,
    getMg4Code,
    getMg5Code,
    getMgZsHevCode,
    getZsCode,
    getZsEvCode,
    normalizeModelName,
} from '../utils';

Vue.component('agricole-fr-calculator-component', {
    props: {
        product: {
            value: String,
        },
        modelDescription: {
            value: String,
        },
        configuratorResult: {
            value: Object,
        },
        agricoleScriptUrl: {
            value: String,
        },
    },
    data() {
        return {
            isMobileDetailsVisible: false,
            isScriptMounted: false,
            monthlyInstallment: null,
            originalPrice: 0,
            calculatorResultForDod: null,
            dodDescription: null,
            footerLegalNotes: null,
            oneTrustCookies: {
                essential: false,
                performance: false,
                functional: false,
                targeting: false,
            },
        };
    },

    mounted() {
        this.mountagricoleScript();

        setTimeout(() => {
            this.handleSetOneTrustConsentCategories();
            this.getDefaultInstallment();
            this.originalPrice = this.$parent.agricoleOriginalPrice;
        }, 2000);
    },

    watch: {
        configuratorResult: {
            handler: debounce(function (newValues) {
                this.updateDefaultInstallment(newValues);
            }, 700),
            deep: true,
        },
        footerLegalNotes(newNotes) {
            if (!newNotes) return;
            EventBus.$emit('agricole-calculator-footer-notes', newNotes);
        },
    },

    computed: {
        mobileDetailsText() {
            return this.isMobileDetailsVisible
                ? 'agricole-fr-calculator__text--mobile--active'
                : '';
        },
        mobileDetailsContainer() {
            return this.isMobileDetailsVisible
                ? 'agricole-details__container--mobile--visible'
                : '';
        },
    },

    methods: {
        handleSetOneTrustConsentCategories() {
            const oneTrustActiveGroups = window.OnetrustActiveGroups;

            if (!oneTrustActiveGroups) return;

            const splitGroups = oneTrustActiveGroups.split(',');
            this.oneTrustCookies.essential = splitGroups.includes('C0001');
            this.oneTrustCookies.performance = splitGroups.includes('C0002');
            this.oneTrustCookies.functional = splitGroups.includes('C0003');
            this.oneTrustCookies.targeting = splitGroups.includes('C0004');
        },
        toggleMobileDetails() {
            this.isMobileDetailsVisible = !this.isMobileDetailsVisible;
        },
        matchModelSlugWithName(modelSlug) {
            return normalizeModelName(modelSlug);
        },
        mountagricoleScript() {
            const agricoleScript = document.createElement('script');
            agricoleScript.async = true;
            agricoleScript.src = this.agricoleScriptUrl;
            document.head.appendChild(agricoleScript);
        },

        getDefaultConfigurationObject(propCarEdition, propCarColor) {
            const carModel = this.getCarModel(this.$parent.model);
            const carEdition =
                propCarEdition || this.$parent.configuratorResult.type.key;
            const carColor =
                propCarColor || this.$parent.configuratorResult.color.key;

            return {
                targetElt: null,
                config: {
                    debug: false,
                    cookies: {
                        essential: this.oneTrustCookies.essential,
                        performance: this.oneTrustCookies.performance,
                        comfort: this.oneTrustCookies.functional,
                        advertising: this.oneTrustCookies.targeting,
                    },
                    marketCode: 'FR',
                    lang: 'fr',
                    touchpoint: 'mg-fr',
                    theme: 'mg',
                    closeBtn: true,
                    onClose: this.onCloseCallback,
                    onSelect: null,
                    vehicle: {
                        img: `https://cdn.mgmotor.eu/images/configurator/${carModel}/${carEdition}/${carColor}.png`,
                        objectType: '1',
                        brandCode: '6F',
                        brandDescription: 'MG',
                        modelCode: this.getModelCode(),
                        modelDescription: ' ',
                        versionCode: this.getVersionCode(),
                        versionDescription: `${this.product} - ${this.$parent.configuratorResult.type.name}: ${this.modelDescription}`,
                        condition: 'N',
                        fuelType: this.getFuelType(),
                        energyClass: '',
                        euroClass: '',
                        originalPrice: this.$parent.agricoleOriginalPrice,
                        ecommercePrice: this.$parent.agricoleEcommercePrice,
                        priceCurrency: 'EUR',
                        originalPriceNoVat: 0,
                        usePriceWithVat: true,
                    },
                },
            };
        },

        initAgricoleScript() {
            if (!('FCABFC3' in window)) return;

            const initObject = this.getDefaultConfigurationObject();
            window.FCABFC3.init(initObject);
        },
        getDefaultInstallment() {
            if (!('FCABFC3' in window)) return;

            const defaultInstalmentObject =
                this.getDefaultConfigurationObject();
            window.FCABFC3.defaultInstalment(defaultInstalmentObject);
        },

        updateDefaultInstallment(configuratorResultValues) {
            this.monthlyInstallment = null;
            if (!('FCABFC3' in window)) return;

            const carEdition = configuratorResultValues.type.key;
            const carColor = configuratorResultValues.color.key;
            const updateObject = this.getDefaultConfigurationObject(
                carEdition,
                carColor,
            );

            updateObject.config.vehicle = {
                ...updateObject.config.vehicle,
                optionalTotalValue:
                    this.$parent.agricoleOriginalPrice - this.originalPrice,
                optionalTotalValueNoVat: 0,
            };

            window.FCABFC3.update(updateObject);
        },

        onCloseCallback(payload) {
            this.calculatorResultForDod = {
                modelName: `${this.matchModelSlugWithName(
                    this.$parent.model,
                )} ${this.configuratorResult.type.name}`,
                discountedPrice: `${this.$parent.agricoleEcommercePrice}`,
                monthlyInstallment:
                    payload.instalment?.monthlyInstalment ?? null,
                firstInstallmentAmount: this.getPayloadDetailByKey(
                    payload.instalment.detailsTable,
                    'firstInstallmentAmount',
                ),
                durationInMonths: payload.instalment?.duration ?? null,
                mileage: this.getPayloadDetailByKey(
                    payload.instalment.detailsTable,
                    'mileage',
                ),
            };

            this.footerLegalNotes = this.getPayloadDetailByKey(
                payload.instalment.detailsTable,
                'parsedLegalNote',
            );
            this.monthlyInstallment =
                payload.instalment?.monthlyInstalment ?? null;

            this.dodDescription = `Lead ayant configuré un/une ${this.calculatorResultForDod.modelName}, au prix remisé de ${this.calculatorResultForDod.discountedPrice} avec une offre de financement en LLD sur le site, avec les caractéristiques suivantes : ${this.calculatorResultForDod.monthlyInstallment}€ par mois, avec un premier apport de ${this.calculatorResultForDod.firstInstallmentAmount}€, d'une durée de ${this.calculatorResultForDod.durationInMonths} mois et un kilométrage de ${this.calculatorResultForDod.mileage} km.`;
        },

        getPayloadDetailByKey(payloadDetails, key) {
            if (!payloadDetails) return null;
            return (
                payloadDetails
                    .filter((detail) => detail.key === key)[0]
                    ?.value.toString() ?? null
            );
        },

        getCarModel(modelSlug) {
            switch (modelSlug) {
                case 'mg4':
                    return 'mg4-2023';
                case 'zs-ev':
                    return 'mg-zs-ev';
                case 'zs':
                    return 'mg-zs-petrol';
                case 'ehs':
                    return 'mg-ehs';
                case 'marvel-r':
                    return 'mg-marvel-r';
                default:
                    return modelSlug;
            }
        },

        getVersionCode() {
            const configuratorVehicle = this.$parent.configuratorResult;
            switch (this.product) {
                case 'EHS':
                    return getEhsCode(configuratorVehicle);
                case 'MARVEL R':
                    return getMarvelRCode(configuratorVehicle);
                case 'ZS EV':
                    return getZsEvCode(configuratorVehicle);
                case 'ZS':
                    return getZsCode(configuratorVehicle);
                case 'MG5':
                    return getMg5Code(configuratorVehicle);
                case 'MG4':
                    return getMg4Code(configuratorVehicle);
                case 'MG3':
                    return getMg3Code(configuratorVehicle);
                case 'MG ZS Hybrid+':
                    return getMgZsHevCode(configuratorVehicle);
                default:
                    return '';
            }
        },

        getModelCode() {
            const versionCode = this.getVersionCode();
            if (versionCode.length < 4) return '';
            return versionCode.substring(0, 4);
        },

        getFuelType() {
            switch (this.product) {
                case 'HS':
                case 'ZS':
                    return '2';
                case 'EHS':
                    return '9';
                default:
                    return '4';
            }
        },

        finishCalculator() {
            this.$parent.completeConfigurator();
            const calculatorResultForDod = {
                isAgricoleCalculation: true,
                dodDescription: this.dodDescription,
            };
            EventBus.$emit(
                'agricole-calculator-finished',
                calculatorResultForDod,
            );
            EventBus.$emit(
                'agricole-calculator-leasing-finished',
                this.monthlyInstallment,
            );
            EventBus.$emit('toggle-agricole-calculator-footer-notes', true);
        },
    },
});
